<script setup>
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import DropdownActions from "@/components/DropdownActions.vue";
import { useMainStore } from "../stores/mainStore";

// eslint-disable-next-line no-unused-vars
const props = defineProps({
    items: {
        type: Array,
        default: () => [],
    },
    isScrollEnabled: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(["cardClick"]);

const route = useRoute();
const router = useRouter();
const mainStore = useMainStore();

async function handleListClick(actionName) {
    if (actionName === "rerun schedule") {
        mainStore.showDuplicateModal = true;
        mainStore.duplicateTermId = route.params.termdateId;
    }

    if (actionName === "delete date range") {
        mainStore.confirmDeleteModal = true;
    }

    if (actionName === "save and publish") {
        await mainStore.saveAndPublish(route.params.termdateId);

        // Redirect to current termdate when updating status, and reset course info to clear draft banner
        router.push({ name: "Advanced term", params: { termdateId: route.params.termdateId } });
        mainStore.reSetCourse = true;
    }

    if (actionName === "delete venue") {
        mainStore.showConfirmDeleteVenueModal = true;
    }
}

function isSimpleRoute() {
    return route.matched[0].name === "Simple";
}

function isServiceRoute() {
    return route.matched[0].name === "Services";
}

const getDropdownList = computed(() => {
    if (isSimpleRoute()) {
        return [
            {
                title: "Delete venue",
                class: "remove",
                dataTest: "list-card-menu-delete",
            },
        ];
    }

    if (isServiceRoute()) {
        return [];
    }

    const haveSessions = mainStore.isTermdateHaveVenueAndSessions(route.params.termdateId);

    const selectedTerm = mainStore.advancedTermDates?.find(
        (td) => td.termdate_id === route.params.termdateId,
    );

    if (haveSessions && selectedTerm.status !== "draft") {
        return [
            {
                title: "Rerun schedule",
            },
            {
                title: "Delete date range",
                class: "remove",
            },
        ];
    }

    if (selectedTerm?.status === "draft") {
        return [
            {
                title: "Save and publish",
                class: "show-dot",
            },
            {
                title: "Delete date range",
                class: "remove",
            },
        ];
    }

    return [
        {
            title: "Delete date range",
            class: "remove",
        },
    ];
});
</script>

<template>
    <div
        :class="{'disabled': !isScrollEnabled}"
        class="d-flex flex-column list-item align-items-center"
        v-if="props.items?.length  > 0"
    >
        <div
            @click="emit('cardClick', item.id)"
            :key="idx" v-for="(item, idx) in props.items"
            data-test="list-card"
            class="mt-2 list_card"
            :class="{'list_card--selected': item.selected}"
        >
            <div class="d-flex flex-column">
                <div class="font-semibold header-grey" data-test="list-card-name">{{ item.name }}</div>
                <div v-show="item.status !== 'draft'" class="list_info" data-test="list-card-info">{{ item.info }}</div>
                <div v-show="item.status === 'draft'" class="list_info list_info-unpublished"
                     data-test="list-card-info-unpublished">Unpublished
                </div>
            </div>
            <div v-show="item.status === 'draft'" class="draft-list-icon" data-test="list-card-icon">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="7.5" cy="7.5" r="7.5" fill="#EB4247"/>
                </svg>
            </div>
            <DropdownActions
                v-if="getDropdownList.length > 0"
                :list="getDropdownList"
                dataTest="list-card-dots"
                @click="handleListClick"
            />
        </div>
    </div>
</template>

<style scoped>
.header-grey {
    color: #272529;
}

.list_card {
    cursor: pointer;
    width: 322px;
    min-height: 62px;
    left: 24px;
    top: 163px;
    background: #FFFFFF;
    border-radius: 10px;
    padding: 8px 16px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.list-item {
    max-height: calc(100vh - 84px - 90px - 53px);
    margin-top: 10px;
    /*overflow-x: hidden;*/
    padding-right: 10px;
    overflow-y: scroll;
    overflow-x: hidden;
    scrollbar-width: none;
}

/*.list-item::-webkit-scrollbar {*/
/*    display: none;*/
/*}*/
.list-item.disabled {
    max-height: unset;
    overflow-y: hidden;
    overflow-x: hidden;
}

.list_info-unpublished {
    color: #EB4247;
}

.font-semibold {
    font-weight: 600 !important;
}

.draft-list-icon {
    margin-left: auto;
    position: relative;
    top: -39%;
    right: -23.7%;
}
</style>
